/**
 * STATUS STACKED AREA CHART
 *
 * Executions-over-time with one stacked area per status. Reads the same
 * TrendResponse.buckets[] payload as TrendLineChart, flattening each
 * bucket's `statusCounts` map into flat columns the chart can consume.
 *
 * Props:
 *   - buckets: Array<{ bucketStart, statusCounts: { Pass, Fail, ... } }>
 *   - height: number (default 240)
 *   - onAreaClick: optional (bucket, statusName) => void
 */

import React, { useMemo } from 'react'
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend
} from 'recharts'
import { token } from '@atlaskit/tokens'
import { getStatusColor } from './statusColors'

const formatDay = (iso) => {
  if (!iso) return ''
  const d = new Date(iso)
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
}

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload || !payload.length) return null
  // payload is one entry per series (status). Sort descending by value for easier scanning.
  const sorted = [...payload].sort((a, b) => (b.value || 0) - (a.value || 0))
  return (
    <div
      style={{
        backgroundColor: token('elevation.surface.overlay', '#FFFFFF'),
        border: `1px solid ${token('color.border', '#DFE1E6')}`,
        borderRadius: '3px',
        padding: '8px 10px',
        fontSize: '12px',
        boxShadow: '0 2px 6px rgba(9, 30, 66, 0.25)'
      }}
    >
      <div style={{ fontWeight: 600, color: token('color.text', '#172B4D') }}>
        {label ? new Date(label).toLocaleDateString(undefined, {
          weekday: 'short', month: 'short', day: 'numeric', year: 'numeric'
        }) : ''}
      </div>
      <div style={{ marginTop: '4px', display: 'flex', flexDirection: 'column', gap: '2px' }}>
        {sorted.filter((e) => e.value > 0).map((entry) => (
          <div key={entry.dataKey} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <span
              style={{
                width: '10px',
                height: '10px',
                borderRadius: '2px',
                backgroundColor: entry.color
              }}
            />
            <span style={{ color: token('color.text.subtle', '#6B778C') }}>
              {entry.dataKey}:
            </span>
            <strong style={{ color: token('color.text', '#172B4D') }}>{entry.value}</strong>
          </div>
        ))}
      </div>
    </div>
  )
}

const StatusStackedAreaChart = ({ buckets = [], height = 240, onAreaClick }) => {
  /**
   * Recharts wants rows of shape `{ bucketStart, Pass: 5, Fail: 1, ... }`.
   * Flatten each bucket's statusCounts map into the row.
   */
  const { data, statusKeys } = useMemo(() => {
    const keys = new Set()
    const rows = (buckets || []).map((b) => {
      const row = { bucketStart: b.bucketStart }
      Object.entries(b.statusCounts || {}).forEach(([k, v]) => {
        row[k] = v
        keys.add(k)
      })
      return row
    })
    // Preserve insertion order from the first bucket that has each key.
    const orderedKeys = []
    for (const b of buckets || []) {
      Object.keys(b.statusCounts || {}).forEach((k) => {
        if (!orderedKeys.includes(k) && keys.has(k)) orderedKeys.push(k)
      })
    }
    return { data: rows, statusKeys: orderedKeys }
  }, [buckets])

  if (!data.length) {
    return (
      <div
        style={{
          height,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: token('color.text.subtle', '#6B778C'),
          fontSize: '13px'
        }}
      >
        No execution history to stack yet.
      </div>
    )
  }

  return (
    <ResponsiveContainer width="100%" height={height}>
      <AreaChart
        data={data}
        margin={{ top: 16, right: 16, left: 0, bottom: 8 }}
        onClick={(e) => {
          if (onAreaClick && e && e.activePayload?.length) {
            onAreaClick(e.activePayload[0].payload)
          }
        }}
      >
        <CartesianGrid stroke={token('color.border', '#DFE1E6')} strokeDasharray="3 3" />
        <XAxis
          dataKey="bucketStart"
          tickFormatter={formatDay}
          tick={{ fontSize: 11, fill: token('color.text.subtle', '#6B778C') }}
          stroke={token('color.border', '#DFE1E6')}
        />
        <YAxis
          tick={{ fontSize: 11, fill: token('color.text.subtle', '#6B778C') }}
          stroke={token('color.border', '#DFE1E6')}
          allowDecimals={false}
        />
        <Tooltip content={<CustomTooltip />} />
        <Legend wrapperStyle={{ fontSize: '12px' }} />
        {statusKeys.map((key, idx) => (
          <Area
            key={key}
            type="monotone"
            dataKey={key}
            stackId="1"
            stroke={getStatusColor(key, idx)}
            fill={getStatusColor(key, idx)}
            fillOpacity={0.7}
            isAnimationActive={false}
          />
        ))}
      </AreaChart>
    </ResponsiveContainer>
  )
}

export default StatusStackedAreaChart
